// school.model.ts

import { Address } from './address.model';
import { AbstractControl, Validators } from '@angular/forms';
import type { LunchConfiguration, ModuleLunchMode } from './lunch.model';
import type { SchoolYearPricing } from './school-pricing.model';
import { Timestamp } from 'firebase/firestore';

/**
 * German districts (Landkreise) in the AWO Care service area
 * Alphabetically sorted for consistency
 */
export type Kreis =
  | 'Darmstadt-Dieburg'
  | 'Frankfurt'
  | 'Gießen'
  | 'Main-Kinzig'
  | 'Odenwald'
  | 'Offenbach'
  | 'Wetterau';

/**
 * District options for form dropdowns
 * Value: stored in database (without prefix)
 * Label: displayed to users (with full name)
 */
export const KREISE_OPTIONS: Array<{ value: Kreis; label: string }> = [
  { value: 'Darmstadt-Dieburg', label: 'Darmstadt-Dieburg' },
  { value: 'Frankfurt', label: 'Frankfurt' },
  { value: 'Gießen', label: 'Gießen' },
  { value: 'Main-Kinzig', label: 'Main-Kinzig' },
  { value: 'Odenwald', label: 'Odenwald' },
  { value: 'Offenbach', label: 'Offenbach' },
  { value: 'Wetterau', label: 'Wetterau' },
];

export interface School {
  id?: string;
  costUnit: string;
  schoolName: string;
  address: Address;
  kreis: Kreis; // District/Landkreis
  contactFirstName: string; // Contact person (Team lead) first name
  contactLastName: string; // Contact person (Team lead) last name
  contactEmail: string; // Contact person email
  contactPhone: string; // Contact person landline
  contactMobile?: string; // Contact person mobile (optional)
  isActive: boolean;
  /** @deprecated migrate via scripts/migrate-lunch-configurations.ts; ersetzt durch lunchSelectionRequired + module.mandatoryLunchId */
  mandatoryLunch?: boolean; // Default false - if true, school MUST offer lunch
  holidayCare?: boolean; // Default false - if true, school offers holiday care
  /**
   * Paktschule („Pakt für den Ganztag"): Unfallschutz über die Unfallkasse
   * Hessen. Default false (Profilschule) — dann sehen Eltern in der
   * Ferienanmeldung den Hinweis zur Unfallversicherung (#1063).
   *
   * Getrennt von `offersPaktklasse`: nicht jede Paktschule führt auch eine
   * Paktklasse.
   */
  paktFuerDenGanztag?: boolean;
  /**
   * Wochenversand der Stammblatt-Änderungen per Mail an die Teamleitung (#1269).
   * Default false; vorerst nur für ausgewählte Testschulen. Setzt nur der Admin.
   */
  teamleadMailEnabled?: boolean;
  /** Ferienliste wöchentlich an die Teamleitung (#1319). Setzt nur der Admin. */
  teamleadHolidayMailEnabled?: boolean;
  /** Default false — wenn true, bietet diese Schule die Paktklasse als Klassenoption an (Issue #604). */
  offersPaktklasse?: boolean;
  modules?: Module[];
  /** @deprecated migrate via scripts/migrate-lunch-configurations.ts; replaced by lunchConfigurations */
  hasLunch?: boolean;
  /** @deprecated wie hasLunch */
  lunch?: Lunch;
  /** Neue Felder ab 2026-05 (Spec: 2026-05-13-flexible-lunch-design.md) */
  lunchSelectionRequired?: boolean;
  lunchConfigurations?: LunchConfiguration[];
  /**
   * Preisschienen je Schuljahr (#746). Genau ein Element hat status 'active'.
   * Fehlt das Feld, ist die Schule noch nicht migriert — dann gelten die
   * Top-Level-Felder als implizite aktive Schiene.
   */
  pricing?: SchoolYearPricing[];
  /**
   * @deprecated Replaced by `documents`. Will be removed after migration script
   * (`scripts/admin/migrate-school-documents.ts`) has run on all environments.
   */
  parentContributionDocument?: ParentContributionDocument;
  /** All school-level documents (Beitragsordnung, Hausordnung, ...). */
  documents?: SchoolDocument[];
  isDemoSchool?: boolean; // Default false - if true, school is a demo/test school for production testing
  holidayCareGroupId?: string; // Reference to holidayCareGroups/{groupId}, denormalized — kept in sync via trigger
  registrationDeadline: Timestamp; // Deadline for registrations (default: Feb 28 of current year)
  // Audit metadata
  createdBy?: string;
  createdByEmail?: string;
  createdOn?: Timestamp;
  lastModifiedBy?: string;
  lastModifiedByEmail?: string;
  lastModifiedSource?: 'employee-ui' | 'function';
}

export interface Module {
  id?: string;
  index: number;
  description: string;
  schedule: Schedule;
  fee: Fee;
  isActive: boolean;
  /** @deprecated migrate via scripts/migrate-lunch-configurations.ts; ersetzt durch lunchMode === 'NONE' */
  disableLunch?: boolean;

  /** Neue Felder ab 2026-05 (Spec: 2026-05-13-flexible-lunch-design.md) */
  lunchMode?: ModuleLunchMode;
  availableLunchIds?: string[];
  mandatoryLunchId?: string;

  /** Zugehörige Preisschiene (#746). Fehlt bei noch nicht migrierten Modulen. */
  schoolYear?: number;
  /** Modul-Dokument, aus dem dieses beim Duplizieren einer Schiene kopiert wurde (#746). */
  sourceModuleId?: string;

  // Audit metadata
  lastModifiedBy?: string;
  lastModifiedByEmail?: string;
}

export interface Fee {
  amount: number;
  currency: 'EUR';
  paymentFrequency: PaymentFrequency;
}

export interface Schedule {
  days: string;
  startTime: string;
  endTime: string;
  note?: string;
}

export interface Lunch {
  description: string;
  externalLunch: boolean;
  url?: string;
  fee?: Fee;
}

export type PaymentFrequency = 'monthly' | 'yearly' | 'weekly' | 'daily';

export interface SchoolDocument {
  /** Stable, client-generated UUID. */
  id: string;
  /** Free-text title, displayed to staff and parents. Required, 1–100 chars after trim. */
  title: string;
  originalFileName: string;
  fileName: string;
  storagePath: string;
  downloadUrl: string;
  mimeType: string;
  fileSize: number;
  /** When true, the document appears in customer-ui and website. */
  visibleToParents: boolean;
  uploadedBy: { uid: string; email: string };
  createdOn: Timestamp;
  lastModifiedOn?: Timestamp;
  lastModifiedBy?: string;
  lastModifiedByEmail?: string;
}

/**
 * @deprecated Use {@link SchoolDocument} instead. Kept for backward compatibility
 * during the migration phase.
 */
export interface ParentContributionDocument {
  originalFileName: string;
  fileName: string;
  storagePath: string;
  downloadUrl: string;
  mimeType: string;
  fileSize: number;
  uploadedBy: {
    uid: string;
    email: string;
  };
  createdOn: Timestamp;
}

export interface Manual {
  id?: string;
  title: string;
  originalFileName: string;
  fileName: string;
  storagePath: string;
  downloadUrl: string;
  mimeType: string;
  fileSize: number;
  // Audit metadata
  createdBy: string;
  createdByEmail: string;
  createdOn: Timestamp;
  lastModifiedBy?: string;
  lastModifiedByEmail?: string;
  lastModifiedSource?: 'employee-ui' | 'function';
}

export function formatScheduleDays(days: string): string {
  if (!days) return '';

  const trimmedDays = days.replace(/\s/g, ''); // Remove all whitespace

  // Check for various weekday formats
  const weekdayPatterns = [
    'Mo,Di,Mi,Do,Fr',
    'Mo,Tu,We,Th,Fr',
    'Monday,Tuesday,Wednesday,Thursday,Friday',
    'Montag,Dienstag,Mittwoch,Donnerstag,Freitag',
  ];

  if (
    weekdayPatterns.some(
      (pattern) => trimmedDays === pattern.replace(/\s/g, '')
    )
  ) {
    return 'Mo - Fr';
  }

  return days;
}

/**
 * Updates lunch form control validation based on school and module settings.
 * Priority: module.disableLunch > school.mandatoryLunch > school.externalLunch
 */
export function updateLunchValidation(
  lunchControl: AbstractControl | null,
  school: School | null,
  module: Module | null
): void {
  if (!lunchControl) return;

  if (module?.disableLunch) {
    lunchControl.clearValidators();
  } else if (
    school?.hasLunch &&
    school?.lunch &&
    (!school.lunch.externalLunch || school.mandatoryLunch)
  ) {
    lunchControl.setValidators([Validators.required]);
  } else {
    lunchControl.clearValidators();
  }
  lunchControl.updateValueAndValidity();
}

/**
 * History record for school/module changes
 * Stored in subcollection: schools/{schoolId}/history/{changeSetId}
 */
export interface SchoolHistoryRecord {
  id?: string;
  timestamp: Timestamp;
  changedBy: string;
  changedByEmail: string;
  changeSource: string;
  entityType: 'school' | 'module';
  entityId?: string; // moduleId for module changes
  entityName?: string; // module description for context
  changeType: 'create' | 'update' | 'delete';
  changes: SchoolFieldChange[];
}

export interface SchoolFieldChange {
  field: string;
  oldValue: unknown;
  newValue: unknown;
}
